import { APP_BASE, APP_HEADER } from "./app";
import { automaticSsoAttemptKey, isAutomaticSsoRootArrival } from "./sso-policy";
import type { AuthResponse, AuthUser, PreviousSignIn, SessionNotice, SessionResponse } from "./types";

/**
 * The browser side of a signed-in session (security §3.1–§3.3), shared by every staff app.
 *
 * - The access token (15 min) lives in the app's `localStorage` key (`ncp.<app>.session`); the refresh token never reaches script: it is an
 *   HttpOnly SameSite=Strict cookie on `/api/auth`, rotated by `POST /api/auth/refresh`.
 * - Activity (pointer, keys, wheel, touch, a tab becoming visible, frame heartbeats) is stored in `<key>.activity`, so every tab of the
 *   app shares it. Timed sessions refresh after activity; continuous ECONIC sessions also renew while idle.
 * - One tab refreshes at a time (`navigator.locks`); a tab that finds a newer token in storage adopts it.
 * - Sessions minted before the strict sign-in (no `sessionId`) keep the old behaviour: no automatic refresh, expiry at `expiresAt`.
 */
export interface Session {
  accessToken: string;
  expiresAt: string;
  user: AuthUser;
  sessionId?: string | null;
  absoluteExpiresAt?: string | null;
  idleTimeoutMinutes?: number | null;
  /** The server enables automatic renewal without an idle or absolute session cutoff. */
  continuous?: boolean;
  amr?: string[] | null;
  previousSignIn?: PreviousSignIn | null;
  notices?: SessionNotice[] | null;
  /** Set by a sign-in in this browser until the arrival message (last sign-in) has been shown once. */
  arrival?: boolean;
  /** When this browser last received a token for the session (ms epoch): the server's idle clock restarts at each refresh. */
  refreshedAt?: number;
}

/** Why a session ended on this device; `/login?reason=` carries it so the sign-in card can say so. */
export type SessionEndReason = "idle" | "expired" | "revoked" | "ended" | "signed_out" | "other_tab";

export type RefreshOutcome = "refreshed" | "adopted" | "ended" | "failed" | "skipped";

export const DEFAULT_IDLE_MINUTES = 30;
/** A refresh is due this long before the access token expires (security §3.3: T − 90 s). */
export const REFRESH_LEAD_MS = 90_000;
const ACTIVITY_THROTTLE_MS = 5_000;

interface ChannelMessage {
  type: "signed-out" | "extended" | "activity";
  reason?: SessionEndReason;
  at?: number;
  sid?: string | null;
}

type Listener = () => void;
type EndListener = (reason: SessionEndReason, origin: "here" | "other_tab") => void;

function parse(raw: string | null): Session | null {
  if (!raw) return null;
  try {
    const s = JSON.parse(raw) as Session;
    return s && typeof s.accessToken === "string" && s.accessToken && s.user ? s : null;
  } catch {
    return null;
  }
}

const ms = (iso: string | null | undefined): number => (iso ? new Date(iso).getTime() : Number.NaN);

/** Problem code of a failed auth call (RFC 7807 `code`, or the minimal-API `error`). */
async function problemCode(res: Response): Promise<string | null> {
  try {
    const body = (await res.clone().json()) as { code?: string; error?: string } | null;
    return body?.code ?? body?.error ?? null;
  } catch {
    return null;
  }
}

export function endReasonOf(code: string | null | undefined): SessionEndReason {
  switch (code) {
    case "session_idle":
      return "idle";
    case "session_expired":
      return "expired";
    case "session_revoked":
    case "refresh_reuse":
      return "revoked";
    default:
      return "ended";
  }
}

/**
 * One controller per app storage key: reads and writes the stored session, tracks activity, refreshes, ends the session (locally and
 * on the server) and tells the other tabs. React state in `AuthProvider` subscribes to it.
 */
export class SessionController {
  private listeners = new Set<Listener>();
  private endListeners = new Set<EndListener>();
  private channel: BroadcastChannel | null = null;
  private inflight: Promise<RefreshOutcome> | null = null;
  private lastMark = 0;
  private ended = false;
  /** Identifies this tab in the `.ended` record, so a tab can tell its own sign-out from another tab's. */
  readonly tabId = Math.random().toString(36).slice(2, 10);

  constructor(
    readonly storageKey: string,
    public apiBase = "",
    readonly channelName = `ncp-session:${storageKey}`,
  ) {
    if (typeof window === "undefined") return;
    this.prepareAutomaticSsoArrival();
    try {
      this.channel = typeof BroadcastChannel === "function" ? new BroadcastChannel(channelName) : null;
      this.channel?.addEventListener("message", (e: MessageEvent<ChannelMessage>) => this.onMessage(e.data));
    } catch {
      this.channel = null;
    }
    window.addEventListener("storage", (e) => {
      if (e.key === this.storageKey || e.key === this.activityKey) this.emit();
    });
  }

  get activityKey(): string {
    return `${this.storageKey}.activity`;
  }

  get endedKey(): string {
    return `${this.storageKey}.ended`;
  }

  /** The sign-out landing stays signed out; expiry and revocation require a deliberate sign-in even on a later visit. */
  automaticSsoBlocked(): boolean {
    try { return window.localStorage.getItem(`${this.storageKey}.sso-manual`) !== null; }
    catch { return true; }
  }

  /** Runs once when this document creates its app controller, before the router can send a root visit to /login. */
  private prepareAutomaticSsoArrival(): void {
    if (!isAutomaticSsoRootArrival(window.location.pathname, window.location.search, APP_BASE)) return;
    try {
      if (parse(window.localStorage.getItem(this.storageKey))) return;
      const blockerKey = `${this.storageKey}.sso-manual`;
      const blocker = window.localStorage.getItem(blockerKey);
      const rawEnd = window.localStorage.getItem(this.endedKey);
      const end = rawEnd ? JSON.parse(rawEnd) as { reason?: string } : null;
      // An older "1" marker is ambiguous after its end notice was consumed. Only a retained explicit sign-out can classify it.
      if (end && end.reason !== "signed_out") return;
      if (blocker !== null && blocker !== "signed_out" && !(blocker === "1" && end?.reason === "signed_out")) return;
      window.sessionStorage.removeItem(automaticSsoAttemptKey(APP_BASE));
      if (blocker !== null) window.localStorage.removeItem(blockerKey);
      if (end?.reason === "signed_out") window.localStorage.removeItem(this.endedKey);
    } catch {
      // Unavailable storage or an unrecognised end record leaves manual sign-in available.
    }
  }

  /**
   * How the last session on this device ended (within 5 minutes), for the sign-in page: whichever of a tab's own navigation, the shared
   * storage event and the BroadcastChannel message arrives first, the reason is the same. `mine` is true when this tab ended it.
   */
  lastEnd(): { reason: SessionEndReason; at: number; mine: boolean } | null {
    try {
      const raw = window.localStorage.getItem(this.endedKey);
      if (!raw) return null;
      const e = JSON.parse(raw) as { reason: SessionEndReason; at: number; by: string };
      if (!(Date.now() - e.at < 5 * 60_000)) return null;
      return { reason: e.reason, at: e.at, mine: e.by === this.tabId };
    } catch {
      return null;
    }
  }

  /** Forgets the end record once the sign-in page has said why. */
  consumeEnd(): void {
    try {
      window.localStorage.removeItem(this.endedKey);
    } catch {
      // ignore
    }
  }

  // ---------------------------------------------------------------- stored session

  /** The stored session, or null. A legacy session past its expiry is dropped; a managed one stays until idle or absolute expiry. */
  read(): Session | null {
    if (typeof window === "undefined") return null;
    let s: Session | null;
    try {
      s = parse(window.localStorage.getItem(this.storageKey));
    } catch {
      return null;
    }
    if (!s) return null;
    const now = Date.now();
    if (!s.sessionId) {
      if (!(ms(s.expiresAt) > now)) {
        this.removeStored();
        return null;
      }
      return s;
    }
    // A managed session stays while its refresh cookie can renew it; the provider ends it at idle or absolute expiry.
    return s;
  }

  write(next: Session): void {
    this.ended = false;
    this.consumeEnd();
    try {
      window.localStorage.setItem(this.storageKey, JSON.stringify(next));
      window.localStorage.removeItem(`${this.storageKey}.sso-manual`);
    } catch {
      // storage full or blocked: the session still lives in memory for this tab
    }
    this.emit();
  }

  /** Stores the answer of a sign-in step, a refresh or a handoff. `arrival` marks a fresh sign-in (the last-sign-in message shows once). */
  store(res: AuthResponse | SessionResponse, options: { arrival?: boolean } = {}): Session {
    const r = res as SessionResponse;
    const previous = this.read();
    const sameSession = !!previous?.sessionId && previous.sessionId === r.sessionId;
    const next: Session = {
      accessToken: r.accessToken,
      expiresAt: r.expiresAt,
      user: r.user,
      sessionId: r.sessionId ?? (sameSession ? previous?.sessionId : null) ?? null,
      absoluteExpiresAt: r.absoluteExpiresAt ?? (sameSession ? previous?.absoluteExpiresAt : null) ?? null,
      idleTimeoutMinutes: r.idleTimeoutMinutes ?? (sameSession ? previous?.idleTimeoutMinutes : null) ?? null,
      continuous: r.continuous ?? (sameSession ? previous?.continuous : false) ?? false,
      amr: r.amr ?? (sameSession ? previous?.amr : null) ?? null,
      previousSignIn: r.previousSignIn ?? (sameSession ? previous?.previousSignIn : null) ?? null,
      notices: options.arrival || !sameSession ? (r.notices ?? null) : (previous?.notices ?? null),
      arrival: options.arrival ? true : sameSession ? previous?.arrival : false,
      refreshedAt: Date.now(),
    };
    if (options.arrival) this.markActivity(true);
    this.write(next);
    return next;
  }

  /** Clears one-shot fields once the UI has shown them. */
  acknowledge(field: "arrival" | "notices"): void {
    const s = this.read();
    if (!s) return;
    if (field === "arrival" && !s.arrival) return;
    if (field === "notices" && !s.notices?.length) return;
    this.write(field === "arrival" ? { ...s, arrival: false } : { ...s, notices: [] });
  }

  private removeStored(): void {
    try {
      window.localStorage.removeItem(this.storageKey);
    } catch {
      // ignore
    }
  }

  getToken = (): string | null => this.read()?.accessToken ?? null;

  /** True for sessions created by the strict sign-in (sid + rotating refresh cookie). */
  isManaged(s: Session | null = this.read()): boolean {
    return !!s?.sessionId;
  }

  idleMs(s: Session | null = this.read()): number {
    if (s?.continuous) return Number.POSITIVE_INFINITY;
    return Math.max(1, s?.idleTimeoutMinutes ?? DEFAULT_IDLE_MINUTES) * 60_000;
  }

  // ---------------------------------------------------------------- activity

  lastActivity(): number {
    try {
      const v = Number(window.localStorage.getItem(this.activityKey));
      return Number.isFinite(v) && v > 0 ? v : 0;
    } catch {
      return 0;
    }
  }

  /** Records activity (throttled to one write per 5 s unless `force`). */
  markActivity(force = false): void {
    if (typeof window === "undefined") return;
    const now = Date.now();
    if (!force && now - this.lastMark < ACTIVITY_THROTTLE_MS) return;
    this.lastMark = now;
    try {
      window.localStorage.setItem(this.activityKey, String(now));
    } catch {
      // ignore
    }
    this.post({ type: "activity", at: now });
    this.emit();
  }

  /** When the session lapses for inactivity (ms epoch), or null for a legacy session. */
  idleDeadline(s: Session | null = this.read()): number | null {
    if (!s || !this.isManaged(s) || s.continuous) return null;
    const last = this.lastActivity() || Date.now();
    return last + this.idleMs(s);
  }

  absoluteDeadline(s: Session | null = this.read()): number | null {
    if (s?.continuous) return null;
    const t = ms(s?.absoluteExpiresAt);
    return Number.isFinite(t) ? t : null;
  }

  // ---------------------------------------------------------------- refresh

  /** Activity happened since this browser last received a token, so a refresh would reflect real use. */
  activeSinceRefresh(s: Session | null = this.read()): boolean {
    if (!s) return false;
    return this.lastActivity() > (s.refreshedAt ?? 0);
  }

  /** Continuous sessions refresh even when the person is reading or has left the tab in the background. */
  shouldRefresh(s: Session | null = this.read()): boolean {
    return this.isManaged(s) && (!!s?.continuous || this.activeSinceRefresh(s)) && this.isStale(s, REFRESH_LEAD_MS);
  }

  /** True when the access token expires within `leadMs`. */
  isStale(s: Session | null = this.read(), leadMs = 10_000): boolean {
    if (!s) return false;
    return !(ms(s.expiresAt) - Date.now() > leadMs);
  }

  /**
   * Rotates the refresh cookie for a new access token. Managed sessions use the cookie (`X-NCP-CSRF: 1`); legacy sessions the bearer
   * route of before. Only one tab refreshes at a time; a tab that finds a newer token adopts it.
   */
  refresh(): Promise<RefreshOutcome> {
    if (this.inflight) return this.inflight;
    const run = async (): Promise<RefreshOutcome> => {
      const before = this.read();
      if (!before) return "skipped";
      const locked = async (): Promise<RefreshOutcome> => {
        const current = this.read();
        if (!current) return "skipped";
        if (current.accessToken !== before.accessToken && !this.isStale(current, REFRESH_LEAD_MS)) return "adopted";
        return this.callRefresh(current, 0);
      };
      const locks = typeof navigator !== "undefined" ? (navigator as Navigator & { locks?: LockManager }).locks : undefined;
      if (locks?.request) return (await locks.request(`ncp-refresh:${this.storageKey}`, locked)) as RefreshOutcome;
      return locked();
    };
    this.inflight = run().finally(() => {
      this.inflight = null;
    });
    return this.inflight;
  }

  private async callRefresh(current: Session, attempt: number): Promise<RefreshOutcome> {
    const managed = this.isManaged(current);
    let res: Response;
    try {
      res = await fetch(`${this.apiBase}/api/auth/refresh`, {
        method: "POST",
        credentials: "include",
        headers: managed
          ? { Accept: "application/json", "X-NCP-CSRF": "1", [APP_HEADER]: APP_BASE }
          : { Accept: "application/json", "Content-Type": "application/json", Authorization: `Bearer ${current.accessToken}`, [APP_HEADER]: APP_BASE },
        body: managed ? undefined : "{}",
      });
    } catch {
      return "failed";
    }
    if (res.ok) {
      const body = (await res.json()) as SessionResponse;
      // A background renewal must not restore a session that was signed out while the request was in flight.
      if (managed && this.read()?.sessionId !== current.sessionId) return "ended";
      this.store(body);
      this.post({ type: "extended", sid: body.sessionId ?? current.sessionId ?? null });
      return "refreshed";
    }
    if (res.status === 409 && attempt === 0) {
      // Another tab rotated the cookie a moment ago: adopt its token, or try once more.
      await new Promise((r) => setTimeout(r, 300));
      const after = this.read();
      if (after && after.accessToken !== current.accessToken) return "adopted";
      return this.callRefresh(after ?? current, 1);
    }
    if (res.status === 401 || res.status === 403) {
      const code = await problemCode(res);
      if (managed) this.end(endReasonOf(code), { revoke: false });
      return "ended";
    }
    return "failed";
  }

  /** A usable access token: refreshed first when it is about to expire (managed sessions only). */
  async freshToken(): Promise<string | null> {
    const s = this.read();
    if (!s) return null;
    if (this.isManaged(s) && this.isStale(s)) {
      const outcome = await this.refresh();
      if (outcome === "ended") return null;
    }
    return this.read()?.accessToken ?? null;
  }

  // ---------------------------------------------------------------- ending

  /**
   * Ends the session on this device: revokes it on the server (best effort, `keepalive` so it survives navigation), clears storage and
   * tells the other tabs. `revoke: false` when the server already ended it (idle, absolute limit, administrator).
   */
  end(reason: SessionEndReason, options: { revoke?: boolean; broadcast?: boolean } = {}): void {
    const s = this.read();
    try {
      const key = `${this.storageKey}.sso-manual`;
      // Clearing an already-ended session must not turn a revocation/expiry or unknown legacy block into a resumable sign-out.
      if (s || reason !== "signed_out" || window.localStorage.getItem(key) === null)
        window.localStorage.setItem(key, reason);
    } catch { /* manual fallback */ }
    const revoke = options.revoke ?? (reason === "signed_out" || reason === "idle");
    if (revoke && s?.accessToken) {
      try {
        void fetch(`${this.apiBase}/api/auth/logout`, {
          method: "POST",
          credentials: "include",
          keepalive: true,
          headers: { Accept: "application/json", Authorization: `Bearer ${s.accessToken}`, "X-NCP-CSRF": "1", [APP_HEADER]: APP_BASE },
        }).catch(() => undefined);
      } catch {
        // ignore
      }
    }
    if (s) {
      try {
        window.localStorage.setItem(this.endedKey, JSON.stringify({ reason, at: Date.now(), by: this.tabId }));
      } catch {
        // ignore
      }
    }
    this.removeStored();
    const first = !this.ended;
    this.ended = true;
    if (options.broadcast !== false && reason !== "other_tab") this.post({ type: "signed-out", reason, sid: s?.sessionId ?? null });
    this.emit();
    if (first && s) for (const l of this.endListeners) l(reason, "here");
  }

  // ---------------------------------------------------------------- subscriptions

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  onEnd(listener: EndListener): () => void {
    this.endListeners.add(listener);
    return () => this.endListeners.delete(listener);
  }

  private emit(): void {
    for (const l of this.listeners) l();
  }

  private post(message: ChannelMessage): void {
    try {
      this.channel?.postMessage(message);
    } catch {
      // ignore
    }
  }

  private onMessage(message: ChannelMessage): void {
    if (!message) return;
    if (message.type === "signed-out") {
      const s = this.read();
      // Another tab already cleared the shared storage; this tab only has to follow.
      this.removeStored();
      this.ended = true;
      this.emit();
      if (s || message.reason) for (const l of this.endListeners) l(message.reason === "signed_out" ? "other_tab" : (message.reason ?? "other_tab"), "other_tab");
      return;
    }
    this.emit();
  }
}

const controllers = new Map<string, SessionController>();

/** The controller of an app's session (one per storage key). `apiBase` and `channelName` apply on first use. */
export function sessionController(storageKey: string, options: { apiBase?: string; channelName?: string } = {}): SessionController {
  let c = controllers.get(storageKey);
  if (!c) {
    c = new SessionController(storageKey, options.apiBase ?? "", options.channelName);
    controllers.set(storageKey, c);
  } else if (options.apiBase !== undefined) {
    c.apiBase = options.apiBase;
  }
  return c;
}

/**
 * Records activity for every session of this page. Frames (desks, custom pages) cannot reach the host's listeners, so the page host
 * calls this on the frame's `activity` heartbeat (shell-ia §11.2).
 */
export function markSessionActivity(): void {
  for (const c of controllers.values()) c.markActivity();
}

/** Where the sign-in page sends someone whose session ended: `/login?reason=idle&from=/m/uae-radar`. */
export function signInUrl(loginPath: string, reason?: SessionEndReason | null, from?: string | null): string {
  const q = new URLSearchParams();
  if (reason && reason !== "signed_out") q.set("reason", reason === "other_tab" ? "other_tab" : reason);
  if (from && from !== loginPath && !from.startsWith(`${loginPath}?`)) q.set("from", from);
  const s = q.toString();
  return s ? `${loginPath}?${s}` : loginPath;
}
